import { createSlice, PayloadAction } from '@reduxjs/toolkit';

/**
 * AHU Board UI state (Phase 6a).
 *
 * `selection` is the React home of the legacy `viewdata`/`ahuboard` shared
 * Backbone model — THE selection contract of PHASE6 inventory §1.11: it is
 * created with `{current_tab:'ahu', current_section:null, selected_sections:[]}`
 * (parity 13) and every `storage:viewdata:change` consumer becomes a selector
 * subscription. The quirk fields (`type`, `component_name`, `component_id`,
 * `component_sitting_id`) are written by api.component.create — including the
 * Plate/Rotary `component_id = id + 1` quirk (§9.3, ported in 6d/6e).
 *
 * `toolbar` re-homes the MainView single-open-window engine (§2.4
 * toolbarElementClick — opacity_0/active/checkbox protocol, parity 21) and the
 * Views pin (sessionStorage `pinned-view`, parity 24 — persistence handled by
 * the caller via ahuBoardStorage).
 *
 * `canvas` holds the chrome-visible canvas state the SHELL reads in 6a
 * (current-view breadcrumb, tunnel-index pill, color mode read side); the
 * three.js consumers attach in 6b via the typed bus.
 */

export type AhuBoardTab = 'ahu' | 'sections' | 'components' | 'accessories';

/** §3.2.2 camera presets (keys of the legacy `views` table). */
export type AhuViewPreset =
  | 'default'
  | 'front_left'
  | 'back_left'
  | 'back_right'
  | 'plan'
  | 'elevation_near_side';

/** data-type keys of the single-open toolbar windows (§2.1 rail). */
export type AhuToolbarWindow =
  | 'model_3d'
  | 'views'
  | 'error_log'
  | 'configure_tunnel'
  | 'title_block';

export interface AhuBoardSelection {
  /** viewdata.current_tab — left-panel tab (§1.11). */
  currentTab: AhuBoardTab;
  /** viewdata.current_section (model → id here; data lives in RTKQ cache). */
  currentSectionId: number | null;
  /** viewdata.selected_sections (multi-select set, ordered like legacy array). */
  selectedSectionIds: number[];
  /** viewdata.type — section type string (e.g. 'fan', name_on_canvas lowercased). */
  type: string | null;
  /** api.component.create quirk fields (§1.11 table). */
  componentName: string | null;
  componentId: number | null;
  componentSittingId: number | null;
}

export interface AhuBoardState {
  selection: AhuBoardSelection;
  /** `select:accessory` receiver state — arms #accessories-ul (§2.4, parity 27). */
  selectedAccessoryId: number | null;
  toolbar: {
    /** Exactly one toolbar window open at a time (parity 21); null = none. */
    openWindow: AhuToolbarWindow | null;
    /** sessionStorage `pinned-view` mirror (parity 24). */
    pinnedViews: boolean;
  };
  canvas: {
    /**
     * #canvas-current-view breadcrumb / views-list active value (parity 25).
     * null = free orbit: the vendored OrbitControls strips both `.active`
     * classes during onPointerMove (legacy behavior); the canvas mirrors that
     * here via setCanvasView(null) so React renders stay consistent (6b).
     */
    view: AhuViewPreset | null;
    /** 360 mode (MainView auto360; canvas consumer attached in 6b). */
    autoRotate: boolean;
    /** localStorage `canvas_color_mode` read side ([NEG]#9 — no eval). */
    colorMode: boolean;
    /** #tunnels-indexes active pill ('all' or tunnel id) — §2.3/§3.2.8. */
    activeTunnelIndexId: number | 'all';
    /**
     * Font + WebGL context ready (6b): the canvas element exists, is sized and
     * holds the grid (parity 7 — legacy removes its preloader in the
     * FontLoader callback, §1.7). It does NOT mean the unit has been drawn —
     * see `painted`.
     */
    ready: boolean;
    /**
     * The BOOT PAINT is done: the first full `render_canvas` pass has drawn
     * the unit's sections and reached the drawing buffer. This — not `ready` —
     * reveals the canvas and collapses the board preloader, so nobody watches
     * an empty canvas waiting for its sections (reported 2026-10-15). The
     * accessory / int-wall / piping draws land after it, on a canvas that
     * already shows the unit. See AhuCanvas's boot-paint gate.
     */
    painted: boolean;
    /**
     * 360 availability — legacy clears `disable`/`disable_rotate` on the
     * #auto_360 li + views-list 360 entry from draw paths (drawTunnelBase /
     * section flows, §3.5). 6b approximation: enabled when the canvas mounts
     * non-empty; the exact base-draw gate lands with 6c geometry.
     */
    rotateEnabled: boolean;
    /** #tunnels-indexes pills shown (canvas renderTunnelsIndexes re-home, 6b). */
    tunnelIndexesVisible: boolean;
  };
  /** #ahu_shy hamburger — collapses the left panel (CSS) + §2.4 width shifts. */
  panelCollapsed: boolean;
  /**
   * §13 row 21 seam — `.main-rotation` class state machine (rotate /
   * rating-mode-active / manual-mode-active / popup-opened). Canvas guards and
   * CompTable read this slice instead of the DOM from 6b on.
   */
  boardMode: {
    flipped: boolean;
    ratingActive: boolean;
    manualActive: boolean;
    popupOpen: boolean;
  };
  /**
   * 6d — §13 SELECTION-SYNC seams (the store-driven halves of the legacy
   * `$().click()` chains; the consuming PANELS land in 6e and subscribe to
   * these fields instead of receiving DOM clicks).
   */
  /** §13 row 2 — `${componentSelector} > form > ul > li:nth-child(1) input` click:
   * api.section.select opens the component form's FIRST subtab on every
   * select. 6e (tab.components) consumes; the 3-step sitting-fallback host
   * resolution (§13 row 1 `resolveComponentHostSectionId`) is the consumer's. */
  componentsTab: {
    openFirstSubtabForSectionId: number | null;
  };
  /** §13 rows 3/13/15 — tab.ahu accordion target:
   * 'tunnel:<id>' (select with tab==='ahu'), 'tunnel:first' (deselect path,
   * row 15), 'lugs' (Base&Lugs via change:active:lugs, row 13). 6e consumes. */
  ahuTab: {
    openAccordion: string | null;
  };
  /** 6e1 perf fix — per-section open subtab (int_wall/d_mount/performance/
   * remarks). Lives in the slice (not SectionForm useState) because only the
   * VISIBLE section form is mounted (legacy keeps all forms in the DOM and
   * hides them via CSS, but Backbone pays no render cost — React must not
   * mount ~90 PaginatedSelects on 15-section units; see 6e1 verify 6e1-3).
   * Keeping it here makes expansion survive form unmount/remount. */
  sectionsTab: {
    openSubtabBySection: Record<number, string | null>;
  };
  /** §13 rows 8/9 — accessory row expansion + subtab ('first' on expand,
   * 'mounting' for the ACCESS_DOOR auto-path). 6e (tab.accessories) consumes;
   * the canvas G34 DOM probes read expandedAccessoryId via hooks NOW. */
  accessoriesTab: {
    expandedAccessoryId: number | null;
    openSubtab: { accessoryId: number; subtab: 'first' | 'mounting' } | null;
  };
  /** §13 rows 10/12 — popup-open seams (mounting popup §49.6, per-row delete
   * popup, DuplicateAccessory §12.1). The popup components land in 6e and
   * subscribe; canvas dblclick / context menus write these NOW. */
  popupSeams: {
    mountingPopupAccessoryId: number | null;
    deleteAccessoryId: number | null;
    duplicateAccessoryId: number | null;
  };
  /** §13 rows 6/7 — `openRating(sectionId)`: rating/selection popup request
   * (the `#show_rating` click chain). Token bumps so 6e can re-open on
   * repeated requests for the same section. */
  rating: {
    requestedSectionId: number | null;
    requestToken: number;
    /**
     * Which selector the chassis currently has OPEN (null = closed): the
     * activate's `name` + `current_model.id`, i.e. the same pair that keys the
     * §30.4 fan-out. The React stand-in for legacy
     * `SelectionMode.ratingModeActive` (selection_mode.js:145) — a PER-COMPONENT
     * flag, which is why this is an identity and not a boolean: re-opening the
     * component that owns the open dialog CLOSES it, while doing the same for a
     * different component switches the dialog over. `name` is part of it
     * because the id space is per-table (an AHU-level selector falls back to
     * the AHU id and could otherwise collide with a component row id).
     * Written by the chassis, read by useComponentForm.
     */
    open: { name: string; modelId: number } | null;
  };
  /** §13 rows 13/14 — lug pane receiving state (`change:active:lugs`):
   * active lug ids + the cross-tunnel retarget. 6e (tab.lifting.lug) renders
   * from this; the canvas publishes via the bus → AhuCanvas subscription. */
  lugs: {
    activeLugIds: Array<number | string>;
    activeTunnelId: number | null;
  };
}

/** Parity 13: selection store initial shape (§1.11). */
const initialSelection: AhuBoardSelection = {
  currentTab: 'ahu',
  currentSectionId: null,
  selectedSectionIds: [],
  type: null,
  componentName: null,
  componentId: null,
  componentSittingId: null,
};

const initialState: AhuBoardState = {
  selection: initialSelection,
  selectedAccessoryId: null,
  toolbar: {
    openWindow: null,
    pinnedViews: false,
  },
  canvas: {
    view: 'default',
    autoRotate: false,
    colorMode: true,
    activeTunnelIndexId: 'all',
    ready: false,
    painted: false,
    rotateEnabled: false,
    tunnelIndexesVisible: false,
  },
  panelCollapsed: false,
  boardMode: {
    flipped: false,
    ratingActive: false,
    manualActive: false,
    popupOpen: false,
  },
  componentsTab: {
    openFirstSubtabForSectionId: null,
  },
  ahuTab: {
    openAccordion: null,
  },
  sectionsTab: {
    openSubtabBySection: {},
  },
  accessoriesTab: {
    expandedAccessoryId: null,
    openSubtab: null,
  },
  popupSeams: {
    mountingPopupAccessoryId: null,
    deleteAccessoryId: null,
    duplicateAccessoryId: null,
  },
  rating: {
    requestedSectionId: null,
    requestToken: 0,
    open: null,
  },
  lugs: {
    activeLugIds: [],
    activeTunnelId: null,
  },
};

const ahuBoardSlice = createSlice({
  name: 'ahuBoard',
  initialState,
  reducers: {
    /** Navy-panel tab switch (ViewTabsNavyPanel §7.2 → viewdata.current_tab). */
    setCurrentTab(state, action: PayloadAction<AhuBoardTab>) {
      state.selection.currentTab = action.payload;
    },

    /**
     * api.section.select core rules (§3.4 #30 / parity 81): updates
     * current_section + selected_sections + tab. `tab` override semantics:
     * explicit tab wins; without one the tab is left ALONE.
     *
     * Parity 103 recorded the opposite ('ahu'→'sections' promotion) because
     * that is what `legacy/backbone-pre-react` did (api.js:89-91). The hybrid
     * branch deleted those three lines in KA-x-user-skill d0f7d0abb1 "Keep
     * active tab on AHU when selecting a section without explicit tab", so
     * picking a section on the canvas now keeps the AHU tab open and lets
     * tab.ahu.js expand that section's tunnel row instead of bouncing the
     * user to CABINET. Every no-`tab` caller goes through api.section.select
     * in legacy too (section.toolbar.js:311/369, tunnels.section.js:549,
     * duplicate_accessory.js:166), so they all inherit the same change; the
     * paths that legacy promotes explicitly (section.toolbar.js:466-474,
     * error log, plenum create) pass `tab` and are unaffected.
     *
     * Ctrl handling (toggle/dedupe) is the caller's job in 6d — this reducer
     * applies the resolved arrays.
     */
    selectSection(
      state,
      action: PayloadAction<{
        sectionId: number;
        type?: string | null;
        tab?: AhuBoardTab | '';
        selectedSectionIds?: number[];
      }>
    ) {
      const { sectionId, type, tab, selectedSectionIds } = action.payload;
      state.selection.currentSectionId = sectionId;
      state.selection.selectedSectionIds = selectedSectionIds ?? [sectionId];
      if (type !== undefined) state.selection.type = type;
      if (tab) state.selection.currentTab = tab;
    },

    /**
     * MainView removeSelection / `remove:selected:section` (§2.4 / §3.4 #3):
     * viewdata reset `{current_section:null, current_tab:'ahu',
     * selected_sections:[]}` (parity 91).
     */
    clearSelection(state) {
      state.selection.currentSectionId = null;
      state.selection.currentTab = 'ahu';
      state.selection.selectedSectionIds = [];
    },

    /** Ctrl multi-select toggle (keyDown §3.2.6 / onSectionAction §3.2.7). */
    toggleSectionInMultiSelect(state, action: PayloadAction<number>) {
      const id = action.payload;
      const list = state.selection.selectedSectionIds;
      const i = list.indexOf(id);
      if (i === -1) list.push(id);
      else list.splice(i, 1);
      state.selection.currentSectionId = list.length ? list[list.length - 1] : null;
      if (!list.length) state.selection.currentTab = 'ahu';
    },

    /** api.component.create viewdata quirk fields (§9.3; consumers in 6d/6e). */
    setComponentSelection(
      state,
      action: PayloadAction<{
        componentName: string | null;
        componentId: number | null;
        componentSittingId: number | null;
        type?: string | null;
      }>
    ) {
      state.selection.componentName = action.payload.componentName;
      state.selection.componentId = action.payload.componentId;
      state.selection.componentSittingId = action.payload.componentSittingId;
      if (action.payload.type !== undefined) state.selection.type = action.payload.type;
    },

    /** `select:accessory(id|null)` receiver (MainView arm/disarm, parity 27). */
    setSelectedAccessory(state, action: PayloadAction<number | null>) {
      state.selectedAccessoryId = action.payload;
    },

    /** toolbarElementClick open path (re-click same window → closed by caller). */
    openToolbarWindow(state, action: PayloadAction<AhuToolbarWindow>) {
      state.toolbar.openWindow = action.payload;
    },
    /** closeWindow (§2.4) — pinned Views re-open handled by the caller. */
    closeToolbarWindow(state) {
      state.toolbar.openWindow = null;
    },
    /** .pin-view toggle (sessionStorage write is the caller's, parity 24). */
    setPinnedViews(state, action: PayloadAction<boolean>) {
      state.toolbar.pinnedViews = action.payload;
    },

    /**
     * defineCanvasViewport active-swap (§2.4; bus emits canvas:set:view) and
     * the canvas onViewChange hook (6b). null = orbit cleared the preset.
     */
    setCanvasView(state, action: PayloadAction<AhuViewPreset | null>) {
      state.canvas.view = action.payload;
    },
    /** auto360() toggle (§2.4; bus emits canvas:rotate). */
    setAutoRotate(state, action: PayloadAction<boolean>) {
      state.canvas.autoRotate = action.payload;
    },
    /** Palette toggle / boot read of `canvas_color_mode` ([NEG]#9). */
    setColorMode(state, action: PayloadAction<boolean>) {
      state.canvas.colorMode = action.payload;
    },
    /** #tunnels-indexes pill click (changeIndex §2.4) / canvas retarget (6b). */
    setActiveTunnelIndex(state, action: PayloadAction<number | 'all'>) {
      state.canvas.activeTunnelIndexId = action.payload;
    },
    /** 6b canvas boot: font + WebGL context ready (parity 7). */
    setCanvasReady(state, action: PayloadAction<boolean>) {
      state.canvas.ready = action.payload;
    },
    /** Boot paint complete → board preloader collapse (see canvas.painted). */
    setCanvasPainted(state, action: PayloadAction<boolean>) {
      state.canvas.painted = action.payload;
    },
    /** 6b: 360 availability (see the canvas.rotateEnabled doc above). */
    setRotateEnabled(state, action: PayloadAction<boolean>) {
      state.canvas.rotateEnabled = action.payload;
    },
    /** 6b canvas renderTunnelsIndexes re-home: pills visibility. */
    setTunnelIndexesVisible(state, action: PayloadAction<boolean>) {
      state.canvas.tunnelIndexesVisible = action.payload;
    },

    /** #ahu_shy hamburger (changeMainRotationWidth side effects in component). */
    setPanelCollapsed(state, action: PayloadAction<boolean>) {
      state.panelCollapsed = action.payload;
    },

    /** §13 row 21 boardMode seam (consumers attach in 6d/6e). */
    setBoardMode(
      state,
      action: PayloadAction<Partial<AhuBoardState['boardMode']>>
    ) {
      Object.assign(state.boardMode, action.payload);
    },

    /**
     * 6d — direct viewdata write (the canvas paths that do NOT route through
     * `select:section`: the already-active-section deselect branch, the
     * empty-space multi-clear, the keyboard Ctrl-deselect — §3.2.6/§3.2.7).
     * Mirrors `stateStorage.models.get('viewdata','ahuboard').set({...})`.
     */
    setSelectionState(
      state,
      action: PayloadAction<{
        currentTab: AhuBoardTab;
        currentSectionId: number | null;
        selectedSectionIds: number[];
      }>
    ) {
      state.selection.currentTab = action.payload.currentTab;
      state.selection.currentSectionId = action.payload.currentSectionId;
      state.selection.selectedSectionIds = action.payload.selectedSectionIds;
    },

    /** §13 row 2 — api.section.select's first-subtab click (every select). */
    setComponentsOpenFirstSubtab(state, action: PayloadAction<number | null>) {
      state.componentsTab.openFirstSubtabForSectionId = action.payload;
    },

    /** §13 rows 3/13/15 — tab.ahu accordion target. */
    setAhuTabAccordion(state, action: PayloadAction<string | null>) {
      state.ahuTab.openAccordion = action.payload;
    },

    /** 6e1 — section-form subtab toggle (state survives form unmount). */
    setSectionSubtab(
      state,
      action: PayloadAction<{ sectionId: number; subtab: string | null }>
    ) {
      state.sectionsTab.openSubtabBySection[action.payload.sectionId] =
        action.payload.subtab;
    },

    /** §13 row 8 — accessory row expansion (tab.accessories openGroup half). */
    setExpandedAccessory(state, action: PayloadAction<number | null>) {
      state.accessoriesTab.expandedAccessoryId = action.payload;
      // openGroup auto-opens the FIRST subtab when checking a row; collapse
      // clears any pending subtab request.
      state.accessoriesTab.openSubtab =
        action.payload == null
          ? null
          : { accessoryId: action.payload, subtab: 'first' };
    },

    /** §13 row 9 — ACCESS_DOOR auto mounting-subtab (300 ms canvas defer). */
    setAccessoryOpenSubtab(
      state,
      action: PayloadAction<{ accessoryId: number; subtab: 'first' | 'mounting' } | null>
    ) {
      state.accessoriesTab.openSubtab = action.payload;
    },

    /** §13 row 10 — mounting-popup request (dblclick HOOD/DAMPER/LOUVER/
     * OPENING/ACCESS_DOOR). Popup component lands in 6e. */
    openMountingPopup(state, action: PayloadAction<number | null>) {
      state.popupSeams.mountingPopupAccessoryId = action.payload;
    },

    /** §13 row 12 — accessory context-menu Delete (MainView
     * openDeleteAccessoryPopup proxy). Per-row popup lands in 6e. */
    openDeleteAccessoryPopup(state, action: PayloadAction<number | null>) {
      state.popupSeams.deleteAccessoryId = action.payload;
    },

    /** §13 row 12 — accessory context-menu Duplicate (MainView
     * showDuplicatePopup proxy → DuplicateAccessory §12.1, 6e). */
    openDuplicateAccessoryPopup(state, action: PayloadAction<number | null>) {
      state.popupSeams.duplicateAccessoryId = action.payload;
    },

    /** §13 rows 6/7 — openRating(sectionId): Enter / dblclick / (6e) navy. */
    openRatingRequest(state, action: PayloadAction<number | null>) {
      state.rating.requestedSectionId = action.payload;
      state.rating.requestToken += 1;
    },

    /**
     * Chassis → store: the selector dialog opened for `{name, modelId}`
     * (payload) or closed (null). One action writes BOTH halves of the legacy
     * state because legacy sets them from the same two events:
     *  - `rating.open` ← `SelectionMode.ratingModeActive`, the per-component
     *    gate that makes a second click a CLOSE (selection_mode.js:176-183).
     *  - `boardMode.ratingActive` ← `#show_rating.active`, which
     *    tabs.navy.panel.js:63-73 adds on `rating_mode:activate` and removes
     *    on `rating_mode:hide`. Its consumers (the navy button's own `active`
     *    class, the canvas `#show_rating…:not(.active)` probe and
     *    `isRatingModeActive`, the psychrometric jump's close-first branch)
     *    were all ported reading this field, but nothing ever set it.
     *
     * The two halves are legacy-independent, hence `navy`. Legacy exempts the
     * UVC selector from the navy `active` class ONLY (tabs.navy.panel.js:68-73)
     * — its `SelectionMode.ratingModeActive` is set for uvc like every other
     * component, because that is what makes a second click on the accessory's
     * own Rating label a CLOSE. The chassis used to skip this whole dispatch
     * for uvc, which silently took `rating.open` out with the navy class and
     * left the uvc label with no way to know its dialog was on screen. Pass
     * `navy: false` to suppress the navy half and keep the per-component gate.
     */
    setRatingDialogOpen(
      state,
      action: PayloadAction<{
        name: string;
        modelId: number;
        navy?: boolean;
      } | null>
    ) {
      state.rating.open = action.payload
        ? { name: action.payload.name, modelId: action.payload.modelId }
        : null;
      state.boardMode.ratingActive =
        action.payload != null && action.payload.navy !== false;
    },

    /** §13 rows 13/14 — change:active:lugs receiving state. */
    setActiveLugs(
      state,
      action: PayloadAction<{ ids: Array<number | string>; tunnelId: number | null }>
    ) {
      state.lugs.activeLugIds = action.payload.ids;
      state.lugs.activeTunnelId = action.payload.tunnelId;
    },

    /** Route unmount — drop per-board state (fresh boot semantics). */
    resetAhuBoard() {
      return initialState;
    },
  },
});

export const {
  setCurrentTab,
  selectSection,
  clearSelection,
  toggleSectionInMultiSelect,
  setComponentSelection,
  setSelectedAccessory,
  openToolbarWindow,
  closeToolbarWindow,
  setPinnedViews,
  setCanvasView,
  setAutoRotate,
  setColorMode,
  setActiveTunnelIndex,
  setCanvasReady,
  setCanvasPainted,
  setRotateEnabled,
  setTunnelIndexesVisible,
  setPanelCollapsed,
  setBoardMode,
  setSelectionState,
  setComponentsOpenFirstSubtab,
  setAhuTabAccordion,
  setSectionSubtab,
  setExpandedAccessory,
  setAccessoryOpenSubtab,
  openMountingPopup,
  openDeleteAccessoryPopup,
  openDuplicateAccessoryPopup,
  openRatingRequest,
  setRatingDialogOpen,
  setActiveLugs,
  resetAhuBoard,
} = ahuBoardSlice.actions;

export default ahuBoardSlice.reducer;
